import { useState, useEffect } from 'react';
import { Loader2, RotateCcw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function ReingestQuarantinedPanel() {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCount, setLoadingCount] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    diagnosticsAPI.quarantinedWebhookCount()
      .then(({ data }) => setCount(data.quarantined_count))
      .catch(() => setCount(null))
      .finally(() => setLoadingCount(false));
  }, []);

  const runReingest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.reingestQuarantinedWebhooks();
      setResult(data);
      setCount(data.still_quarantined_count + data.no_payload_count);
      toast.success(`Re-ingest complete: ${data.resolved_count} resolved`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Re-ingest Quarantined Webhook Bookings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Re-processes webhook bookings that were quarantined due to missing price fields.
          Retrieves the original raw payload from the webhook log and runs it through the
          corrected parser. Idempotent: safe to run multiple times.
        </p>

        <div className="flex items-center gap-3">
          <Button onClick={runReingest} disabled={loading || (count === 0 && !result)}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Re-ingest
          </Button>
          {loadingCount ? (
            <span className="text-xs text-muted-foreground">Loading count...</span>
          ) : count !== null ? (
            <Badge variant={count > 0 ? 'destructive' : 'outline'} className="text-xs">
              {count} quarantined
            </Badge>
          ) : null}
        </div>

        {result && (
          <div className="rounded-lg border p-3 bg-muted/30 space-y-3">
            <div className="flex items-center gap-2">
              {result.resolved_count > 0 ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              )}
              <span className="text-sm font-medium">Re-ingest complete</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Quarantined</p>
                <p className="text-sm font-medium tabular-nums">{result.quarantined_count}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Resolved</p>
                <p className="text-sm font-medium tabular-nums text-green-600 dark:text-green-400">
                  {result.resolved_count}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Still quarantined</p>
                <p className="text-sm font-medium tabular-nums">
                  {result.still_quarantined_count}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">No payload</p>
                <p className="text-sm font-medium tabular-nums">{result.no_payload_count}</p>
              </div>
            </div>

            {result.records && result.records.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-1 pr-3">Booking ID</th>
                      <th className="py-1 pr-3">Property</th>
                      <th className="py-1 pr-3">Total Price</th>
                      <th className="py-1 pr-3">Net Revenue</th>
                      <th className="py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.records.map((r) => (
                      <tr key={r.uplisting_id} className="border-b border-muted/50">
                        <td className="py-1 pr-3 font-mono">{r.uplisting_id}</td>
                        <td className="py-1 pr-3">{r.property_name}</td>
                        <td className="py-1 pr-3 tabular-nums">
                          {r.total_price != null ? r.total_price.toFixed(2) : '-'}
                        </td>
                        <td className="py-1 pr-3 tabular-nums">
                          {r.net_revenue != null ? r.net_revenue.toFixed(2) : '-'}
                        </td>
                        <td className="py-1">
                          <Badge
                            variant={r.status === 'resolved' ? 'outline' : 'destructive'}
                            className="text-[10px]"
                          >
                            {r.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
